import { keys } from '../../utilities/translator/translation_keys';

// keys need to be the name of the model
//
// `description` is hand-authored flavor text shown in the Permissions admin
// page
//
// `overrides` (on an option, not a module) is a short list of relationships
// that already grant this specific action for free, shown as chips right on
// the option row — most of this app's real access control runs on these
// relationships (an assignee acting on their own item, a workspace admin,
// etc.), not on grants like this one
export const modules = [
  {
    title: keys.common.OBSERVATION,
    keys: ['observation', 'inspectionTemplate'],
    icon: 'content_paste_search',
    automaticActions: [
      {
        title: 'Create an observation',
        description: 'Anyone can create one.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description:
          'Lets someone edit, review, or close an observation after it’s created.',
      },
      {
        title: keys.permissions.VIEW,
        actions: ['VIEW'],
        description:
          'Lets someone see every observation across every workspace with no privacy restriction.',
      },
      {
        title: keys.permissions.MANAGE_TEMPLATE,
        actions: ['MANAGE_TEMPLATE|OBSERVATION'],
        description:
          'Lets someone build and edit the templates used for observation forms.',
        overrides: ['Workspace Admin'],
      },
      {
        title: keys.permissions.COMPANY_WIDE,
        actions: ['COMPANY_WIDE'],
        description:
          'Widens visibility from "my workspaces" to every workspace in the company.',
      },
    ],
  },
  {
    title: keys.common.INCIDENT,
    keys: ['incident', 'inspectionTemplate'],
    icon: 'fmd_bad',
    automaticActions: [
      {
        title: 'Report an incident',
        description: 'Anyone can create one.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description:
          'Lets someone edit an incident’s fields and manage its participants, labels, and reviewers.',
        overrides: ['Reviewer'],
      },
      {
        title: keys.permissions.CLOSE,
        actions: ['CLOSE'],
        description:
          'Lets someone mark an incident Closed/Complete. Even a reviewer or workspace admin can’t close an incident without this grant specifically.',
      },
      {
        title: keys.permissions.VIEW_ALL,
        actions: ['VIEW_ALL'],
        description:
          'Removes the private-record restriction — normally a private incident is only visible to its creator, its participants, its reviewers, or the creator’s supervisor.',
      },
      {
        title: keys.permissions.MANAGE_TEMPLATE,
        actions: ['MANAGE_TEMPLATE|INCIDENT'],
        description:
          'Lets someone build and edit the templates used for incident report forms.',
        overrides: ['Workspace Admin'],
      },
      {
        title: keys.permissions.COMPANY_WIDE,
        actions: ['COMPANY_WIDE'],
        description:
          'Widens incident visibility to every workspace in the company, not just workspaces this person belongs to.',
      },
      {
        title: keys.permissions.DELETE,
        actions: ['DELETE'],
        description: 'Lets someone delete an incident.',
        overrides: ['Creator'],
      },
    ],
  },
  {
    title: keys.common.ACTION_ITEMS,
    keys: ['actionItem'],
    icon: 'task_alt',
    automaticActions: [
      {
        title: 'Create an action item',
        description: 'Anyone can create one.',
      },
      {
        title: 'Edit or delete an action item',
        description:
          'Automatic if you’re the creator, the assignee, or a participant — or a workspace admin, for items in your workspace.',
        overrides: ['Creator', 'Assignee', 'Participants', 'Workspace Admin'],
      },
      {
        title: 'Edit action item comment',
        description:
          'Editing or deleting a comment is limited to whoever wrote it.',
        overrides: ['Creator'],
      },
    ],
    options: [],
  },
  {
    title: keys.common.ASSESSMENTS,
    keys: ['inspection', 'inspectionTemplate'],
    icon: 'inventory',
    automaticActions: [
      {
        title: 'Create an assessment',
        description: 'Anyone can start one.',
      },
      {
        title: 'Edit an assessment’s items',
        description:
          'Open to everyone — a user gets added as a participant once they contribute to the assessment.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description:
          'Lets someone submit a review and, if approved, publish the assessment — even without being personally assigned as its reviewer.',
      },
      {
        title: 'Publish (finalize)',
        actions: ['PUBLISH'],
        description: 'Lets someone publish (finalize) an assessment directly.',
        overrides: ['Participants', 'Reviewer'],
      },
      {
        title: keys.permissions.MANAGE_TEMPLATE,
        actions: ['MANAGE_TEMPLATE'],
        description:
          'Lets someone build and edit assessment templates, including company-wide ones. Workspace admins already have this for templates in their own workspace.',
        overrides: ['Workspace Admin'],
      },
      {
        title: keys.permissions.DELETE,
        actions: ['DELETE'],
        description: 'Lets someone delete an assessment.',
        overrides: ['Creator', 'Workspace Admin'],
      },
    ],
  },
  {
    title: keys.common.ASSETS,
    keys: ['vehicle'],
    icon: 'local_shipping',
    options: [
      {
        title: keys.permissions.MANAGE,
        actions: ['CREATE', 'UPDATE', 'DELETE', 'WORKSPACE'],
        description:
          'Lets someone create assets, edit any asset’s details, move an asset between workspaces, or delete one they created.',
        overrides: ['Workspace Admin', 'Creator'],
      },
      {
        title: keys.permissions.COMPANY_WIDE,
        actions: ['COMPANY_WIDE'],
        description:
          'Widens asset-related workspace access to the whole company rather than just workspaces this person belongs to.',
      },
    ],
  },

  {
    title: keys.common.TASKS,
    keys: ['task'],
    icon: 'assignment_turned_in',
    options: [
      {
        title: keys.permissions.MANAGE,
        actions: ['CREATE', 'UPDATE', 'DELETE'],
        description: 'Lets someone create, edit, or delete a scheduled task.',
      },
    ],
  },

  {
    title: keys.common.FILES,
    keys: ['file'],
    icon: 'folder',
    options: [
      {
        title: keys.permissions.MANAGE_GLOBAL,
        actions: ['CREATE', 'UPDATE', 'DELETE'],
        description:
          'Lets someone upload a general file not attached to a workspace or certificate, and edit or delete files they created.',
        overrides: ['Creator'],
      },
      {
        title: keys.permissions.MANAGE_WORKSPACE,
        actions: ['UPDATE_WORKSPACE', 'DELETE_WORKSPACE'],
        description:
          'Lets someone edit or delete a workspace-level file they didn’t create.',
        overrides: ['Creator', 'Workspace Admin'],
        uiNote:
          'The app doesn’t actually check creator or workspace-admin status here — Rename and Delete are shown to anyone who can see the file, with no relationship check at all. The backend enforces the real restriction if either is attempted.',
      },
      {
        title: 'Manage AI (The Hub)',
        actions: ['CREATE_AI', 'UPDATE_AI', 'DELETE_AI'],
        description:
          'Lets someone upload, edit, or delete files indexed for AI search in The Hub — separate from Manage Global.',
        flag: 'enableVectorDatabase',
      },
      {
        title: keys.permissions.VIEW_ALL,
        actions: ['VIEW_ALL'],
        description:
          'Widens file visibility on the Workspace tab to every workspace in the company.',
      },
    ],
  },
  {
    title: keys.common.ROLES,
    keys: ['requirement', 'file'],
    icon: 'card_membership',
    options: [
      {
        title: keys.permissions.ADD_REQUIREMENT,
        actions: ['CREATE', 'UPDATE'],
        description: 'Lets someone manage certifications/requirements.',
        uiNote:
          'The app doesn’t gate this at all — the Certifications screen (reachable from the sidebar behind only a feature flag, not a permission) lets any company member create and attach requirements to a Role, and can even expose a Role’s permission checkboxes as editable. Nothing in the UI enforces the Admin/grant-only rule described above.',
      },
    ],
  },
  {
    title: keys.common.USERS,
    keys: ['user'],
    icon: 'person_outline',
    options: [
      {
        title: keys.permissions.INVITE,
        actions: ['INVITE'],
        description:
          'Lets someone invite a new user to the company, and gates access to the Users table page from the sidebar.',
      },
      {
        title: keys.permissions.EDIT,
        actions: ['UPDATE'],
        description:
          'Lets someone edit another user’s basic profile info. Everyone can already edit their own.',
        overrides: ['Themselves', 'Supervisor'],
      },
      {
        title: keys.permissions.VIEW_DETAILS,
        actions: ['VIEW_DETAILS'],
        description: 'Lets someone view another user’s full profile details.',
        overrides: ['Themselves', 'Their supervisor'],
      },
      {
        title: 'Manage Permissions',
        actions: ['UPDATE_PERMISSION'],
        description:
          'Lets someone grant or revoke another user’s individual permission overrides. Be deliberate about who else gets it, since it can be used to grant further permissions in turn.',
        overrides: ['Their supervisor', 'Workspace Admin'],
      },
    ],
  },
  {
    title: keys.common.AUDIT,
    keys: ['audit', 'inspectionTemplate', 'actionItemExtension'],
    icon: 'checklist',
    options: [
      {
        title: keys.permissions.MANAGE_AUDIT,
        actions: ['CREATE', 'UPDATE', 'DELETE'],
        description:
          'Lets someone create or delete Audits, and is also what makes the Audit section itself appear in the sidebar.',
      },
      {
        title: keys.permissions.MANAGE_CHECKLIST,
        actions: ['MANAGE_TEMPLATE|AUDIT'],
        description:
          'Lets someone build and edit the templates used for audit checklists.',
        overrides: ['Workspace Admin'],
      },
    ],
    flag: 'auditsAndCorrectiveActions',
  },
  {
    title: keys.common.CORRECTIVE_ACTION,
    keys: ['correctiveAction'],
    icon: 'engineering',
    automaticActions: [
      {
        title: 'Reassign a CPA',
        description:
          'Restricted to a company Admin at any time, or the CPA’s own creator while it’s still in the interim stage.',
        overrides: ['Creator'],
      },
      {
        title: 'Remove an attachment from a Corrective Action',
        description: 'Anyone can add one.',
        overrides: ['Assignee'],
      },
      {
        title: 'Action item extensions',
        description:
          'Requesting an extension is limited to the action item’s assignee. Approving or denying one is restricted to the CPA’s own creator (or company Admin) — not a grantable permission.',
        overrides: ['Assignee', 'Creator'],
      },
    ],
    options: [
      {
        title: keys.permissions.MANAGE,
        actions: ['CREATE', 'DELETE'],
        description: 'Lets someone create or delete a Corrective Action.',
      },
      {
        title: 'Manage participants',
        actions: ['MANAGE_PARTICIPANTS'],
        description:
          'Lets someone add or remove a Corrective Action’s participants.',
      },
    ],
    flag: 'auditsAndCorrectiveActions',
  },
  {
    title: keys.common.TIMECARDS,
    keys: ['timecard'],
    icon: 'timer',
    automaticActions: [
      {
        title: 'Create and submit a timecard',
        description: 'Anyone can create one.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description: 'Lets someone approve, deny, or mark a timecard paid.',
        overrides: ['Supervisor', 'Workspace Admin'],
      },
      {
        title: keys.permissions.VIEW_ALL,
        actions: ['VIEW_ALL'],
        description:
          'Lets someone view all timecards within the company, not just their direct reports.',
      },
    ],
    flag: 'enableTimecards',
  },
  {
    title: keys.common.EXPENSES,
    keys: ['expense'],
    icon: 'paid',
    automaticActions: [
      {
        title: 'Create and submit an expense',
        description: 'Anyone can create one.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description: 'Lets someone approve or deny an expense.',
        overrides: ['Supervisor', 'Workspace Admin'],
      },
      {
        title: 'Mark as paid',
        actions: ['PAY'],
        description:
          'Lets someone mark an approved expense as paid/complete. Separate from Review — approving an expense doesn’t include this.',
      },
      {
        title: keys.permissions.VIEW_ALL,
        actions: ['VIEW_ALL'],
        description: 'Lets someone view every company member’s expenses.',
      },
    ],
    flag: 'enableExpenses',
  },
  {
    title: keys.common.ANNOUNCEMENTS,
    keys: ['announcement'],
    icon: 'campaign',
    automaticActions: [
      {
        title: 'View announcements',
        description:
          'Anyone in the company sees published announcements on their dashboard — this doesn’t require the grant below.',
      },
    ],
    options: [
      {
        title: keys.permissions.MANAGE,
        actions: ['CREATE', 'UPDATE', 'DELETE'],
        description:
          'Lets someone create, edit, publish, and delete announcements, and is also what makes the Announcements management page appear in the sidebar.',
      },
    ],
  },
  {
    title: 'Check Ins',
    keys: ['checkIn'],
    icon: 'accessibility_new',
    automaticActions: [
      {
        title: 'Create or view check-ins',
        description:
          'Anyone can start a check-in, and see every check-in company-wide.',
      },
      {
        title: 'Edit or end a check-in',
        description:
          'Limited to its creator, the check-in’s responsible user, or its supervisor (if one is set) — or company Admin. This is a fixed rule, not a grantable permission.',
        overrides: ['Creator', 'Responsible User', 'Supervisor'],
      },
    ],
    options: [],
    flag: 'enableLoneWorker',
  },
  {
    title: keys.dashboard.TOOLBOX_TALK,
    keys: ['toolboxTalk'],
    icon: 'home_repair_service',
    options: [
      {
        title: keys.permissions.MANAGE,
        actions: ['CREATE', 'UPDATE', 'DELETE'],
        description:
          'Lets someone create, edit, delete, or schedule a toolbox talk.',
        overrides: ['Workspace Admin'],
      },
      {
        title: keys.permissions.COMPANY_WIDE,
        actions: ['COMPANY_WIDE'],
        description:
          'Widens toolbox talk management to every workspace in the company.',
      },
    ],
  },
  {
    title: keys.common.INSIGHTS,
    keys: ['report'],
    icon: 'insights',
    options: [
      {
        title: 'Access',
        actions: ['VIEW'],
        description:
          'Lets someone access the Insights/Analytics area and build custom reports.',
        uiNote:
          'The main Insights page now checks this on the page itself, matching the sidebar. The deeper Analytics (custom report builder) and Custom Reports pages it links to still don’t have their own check yet, so someone who already has a link into one of those can still get in directly without this grant.',
      },
    ],
    flag: 'enableCustomReports',
  },
  {
    title: keys.common.RISK_REGISTRY,
    keys: ['riskRegistry', 'risk', 'mitigation'],
    icon: 'health_and_safety',
    automaticActions: [
      {
        title: 'Create, edit, and delete risks and mitigations',
        description:
          'Currently open to everyone in the company — see the note below about this being an intentional, known gap rather than an oversight.',
        uiNote:
          'The sidebar link is oddly gated behind Review below — a permission the backend doesn’t actually check for this module — so most people won’t stumble onto this page through normal navigation. But it’s not a real barrier: anyone who finds the URL gets full, unrestricted create/edit/delete once there, matching the gap above exactly.',
      },
    ],
    options: [
      {
        title: keys.permissions.REVIEW,
        actions: ['REVIEW'],
        description:
          'Displayed for consistency with other modules, but as of this review the Risk Registry backend doesn’t check any permission at all — every authenticated company member can already create, edit, and delete risks and mitigations regardless of this setting. Known and intentionally left as-is for now (feature is in limited use with one client, not flagged as a concern by them).',
      },
    ],
    // Hidden from the Permissions page for now, same reasoning as the
    // sidebar nav link — there's no real access control on this module's
    // backend yet, so showing a "Review" grant here is misleading (it does
    // nothing). Restore to 'enableHazardLog' once real permissions exist.
    flag: 'disabledUntilRiskRegistryHasRealPermissions',
  },
  {
    title: keys.common.QUALITY,
    keys: ['quality'],
    icon: 'auto_awesome',
    options: [
      {
        title: 'Access',
        actions: ['VIEW'],
        description:
          'Shows the Quality link in the sidebar and gives access to the module.',
      },
    ],
    flag: 'enableQualityModule',
  },
  {
    title: 'Permissions Manager',
    keys: ['permissionManager'],
    icon: 'admin_panel_settings',
    options: [
      {
        title: 'Manage Permissions',
        actions: ['VIEW'],
        description:
          'Allows access to permission page and manage all permission throughout the app.',
      },
    ],
  },
];

export const updatePermissions = ({
  object: { id, permissions = {} },
  objectIdKey = '',
  mutation,
  options: { key, actions, type },
  setResult = () => {},
  action = 'ADD',
}) => {
  let updatedPermissions = { ...permissions };
  let updatedActions = actions;
  if (key === 'inspection' || key === 'inspectionTemplate') {
    updatedActions = updatedActions.map((action) =>
      action.includes('|') ? action : `${action}|${type}`,
    );
  }

  const formattedPermissions =
    action === 'ADD'
      ? Array.from(
          new Set([...(updatedPermissions?.[key] ?? []), ...updatedActions]),
        )
      : [
          ...updatedPermissions?.[key]?.filter(
            (p) => !updatedActions.includes(p),
          ),
        ];

  if (key === 'timecard' || key === 'expense') {
    updatedPermissions.activity = action === 'ADD' ? ['UPDATE'] : [];
  }

  if (key === 'audit') {
    updatedPermissions.correctiveAction =
      action === 'ADD'
        ? ['CREATE', 'UPDATE', 'DELETE', 'MANAGE_PARTICIPANTS']
        : [];
    updatedPermissions.actionItemExtension = action === 'ADD' ? ['UPDATE'] : [];
  }

  if (key === 'requirement') {
    updatedPermissions.requirement =
      action === 'ADD' ? ['CREATE', 'UPDATE'] : [];
    updatedPermissions.file =
      action === 'ADD' ? ['CREATE_CERTIFICATE', 'CREATE', 'UPDATE'] : [];
  }

  mutation({
    [objectIdKey]: id,
    permissions: {
      ...updatedPermissions,
      [key]: formattedPermissions,
    },
    onComplete: ({ data }) => setResult(data),
  });
};

export const templateModuleKeys = ['INCIDENT', 'AUDIT', 'OBSERVATION'];

export const qualityTemplateModuleKeys = ['QUALITY', 'MOC'];
